import React, { FunctionComponent } from 'react';
import { useFormContext, useStepper } from '../../../../hooks';
import {
  CalculatorAdditionalPaymentFormFields,
  CalculatorAdditionalPaymentResultData
} from './types';
import useMediaQuery from '../../../../hooks/use-media';
import { mainMediaMobile, utilNumberToCurrency, utilStringToNumber } from '../../../../utils';
import { StepperPrevious } from '../../../shared/general';
import { FormattedMessage, useIntl } from 'react-intl';
import { utilsAdditionalPaymentGetTranslationFormOptions } from './utils';
import CalculatorAdditionalPaymentChart from './calculator-additional-payment-chart';

export const CalculatorAdditionalPaymentResult: FunctionComponent = () => {
  const { form, result, updateResult } = useFormContext<
    CalculatorAdditionalPaymentResultData,
    CalculatorAdditionalPaymentFormFields
  >();
  const intl = useIntl();
  const isMobile = useMediaQuery(mainMediaMobile);
  const { steps, currentStep, setCurrentStep, setStepActive } = useStepper();

  const handleBackClick = () => {
    if (currentStep && currentStep !== 0) {
      const id = currentStep - 1;
      setStepActive(currentStep, true);
      setCurrentStep(id);
      updateResult(null);
    }
  };

  if (!result) {
    return null;
  }

  const { monthlyPaymentSavings, monthlyPayments, residualDebt, interestCosts } = result;

  return (
    <div>
      {isMobile && currentStep && (
        <div>
          <StepperPrevious
            currentStep={currentStep}
            totalSteps={steps.length}
            title={`Vorige stap`}
            handleBackClick={handleBackClick}
          />
        </div>
      )}
      <div className={`rounded shadow shadow-shadow-500 bg-white mb-4`}>
        <div className={`p-8 w-full`}>
          <div>
            <p className={`text-gray-500`}>
              <FormattedMessage
                id={`result.monthlySavings`}
                defaultMessage={`Besparing maandlasten (bruto)`}
              />
            </p>
            <h3>{utilNumberToCurrency(monthlyPaymentSavings as number, true, 0)}</h3>

            <div className={`overflow-x-auto`}>
              <table className={`my-8 -ml-8 border-separate border-spacing-x-8 border-spacing-y-2`}>
                <caption className={`text-left ml-8 mb-4 p-4 bg-gray-100 rounded`}>
                  <FormattedMessage
                    id={`result.extraPayment.title`}
                    defaultMessage={'Extra afgelost: {amount} op {paymentType}'}
                    values={{
                      amount: utilNumberToCurrency(
                        utilStringToNumber(form.additionalPayment),
                        true,
                        0
                      ),
                      paymentType: utilsAdditionalPaymentGetTranslationFormOptions(
                        intl,
                        'paymentType',
                        form.paymentType
                      )
                    }}
                  />
                </caption>
                <tbody className={`-ml-2`}>
                  <tr>
                    <th scope="row"></th>
                    <td>
                      <FormattedMessage
                        id={`result.table.extraPayment.title`}
                        defaultMessage={`Maandlasten`}
                      />
                    </td>
                    <td>
                      <FormattedMessage
                        id={`result.table.residualDebt.title`}
                        defaultMessage={`Restschuld`}
                      />
                    </td>
                    <td>
                      <FormattedMessage
                        id={`result.table.interestCosts.title`}
                        defaultMessage={`Rentekosten`}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" className={`whitespace-nowrap`}>
                      <FormattedMessage
                        id={`result.table.before`}
                        defaultMessage={'Voor aflossen'}
                      />
                    </th>
                    <td>{utilNumberToCurrency(monthlyPayments.old, true, 0)}</td>
                    <td>{utilNumberToCurrency(residualDebt.old, true, 0)}</td>
                    <td>{utilNumberToCurrency(interestCosts.old, true, 0)}</td>
                  </tr>
                  <tr>
                    <th scope="row" className={`whitespace-nowrap`}>
                      <FormattedMessage id={`result.table.after`} defaultMessage={'Na aflossen'} />
                    </th>
                    <td>{utilNumberToCurrency(monthlyPayments.new, true, 0)}</td>
                    <td>{utilNumberToCurrency(residualDebt.new, true, 0)}</td>
                    <td>{utilNumberToCurrency(interestCosts.new, true, 0)}</td>
                  </tr>
                  <tr>
                    <th scope="row" className={`whitespace-nowrap`}>
                      <FormattedMessage
                        id={`result.table.difference`}
                        defaultMessage={'Verschil'}
                      />
                    </th>
                    <td className={`border-0 border-t-[1px] border-solid border-black`}>
                      {utilNumberToCurrency(monthlyPayments.difference, true, 0)}
                    </td>
                    <td className={`border-0 border-t-[1px] border-solid border-black`}>
                      {utilNumberToCurrency(residualDebt.difference, true, 0)}
                    </td>
                    <td className={`border-0 border-t-[1px] border-solid border-black`}>
                      {utilNumberToCurrency(interestCosts.difference, true, 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <CalculatorAdditionalPaymentChart data={result}></CalculatorAdditionalPaymentChart>
            </div>
          </div>
          {!isMobile && currentStep && (
            <div className={`absolute right-16 top-8`}>
              <StepperPrevious
                variant={`horizontal`}
                title={`Vorige stap`}
                handleBackClick={handleBackClick}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
